import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { useRoutesContext } from "@providers/RoutesProvider";
import { useNotificationContext } from "@providers/NotificationProvider";
import { useGetAllBookings } from "@store/actions/bookingActions";
import { useGetStats } from "@store/actions/analyticsActions";
import {
  GET_DASHBOARD_TASKS,
  GET_DASHBOARD_CATALOG,
  GET_DASHBOARD_CONVERSATION,
} from "@store/operations/bookingDashboardOperations";
import { buildDashboardPayload } from "@components/booking/dashboard/dashboardData";
import BookingDashboardNativeDialog from "@components/booking/dashboard/BookingDashboardNativeDialog";
import useBookingDashboardActions from "@store/actions/useBookingDashboardActions";
import { useGenerateQuote } from "@store/actions/quoteActions";
import { useGenerateOptions } from "@store/actions/bookingActions";
import BookingDashboard from "@components/booking/dashboard/BookingDashboard";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BOOKING_POLL_INTERVAL = 10000;
const TASK_POLL_INTERVAL = 60000;
const STATS_POLL_INTERVAL = 60000;

export default function BookingDashboardReviewPage() {
  const client = useApolloClient();
  const history = useHistory();
  const { routes } = useRoutesContext();
  const { showNotification } = useNotificationContext();
  const [generateQuote] = useGenerateQuote();
  const [generateOptions] = useGenerateOptions();
  const historyAvailable = routes.some((route) => route.path === "/bookings/:bookingId/history");
  const currentPayload = useRef(null);
  const nativeRequestRef = useRef(null);
  const nativeSequence = useRef(0);
  const retryActive = useRef(false);
  const mounted = useRef(true);
  const [refreshEnabled, setRefreshEnabled] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [nativeRequest, setNativeRequest] = useState(null);

  const bookings = useGetAllBookings(refreshEnabled ? BOOKING_POLL_INTERVAL : 0);
  const tasks = useQuery(GET_DASHBOARD_TASKS, {
    pollInterval: refreshEnabled ? TASK_POLL_INTERVAL : 0,
    errorPolicy: "none",
  });
  const catalog = useQuery(GET_DASHBOARD_CATALOG, { errorPolicy: "none" });
  const statistics = useGetStats(refreshEnabled ? STATS_POLL_INTERVAL : 0);

  const result = useMemo(() => {
    if (bookings.error || tasks.error || catalog.error || statistics.error) {
      return { error: "Bookings could not be loaded. Please try again.", payload: null };
    }
    if (!bookings.data || !tasks.data || !catalog.data || !statistics.data) {
      return { error: null, payload: null };
    }
    try {
      const dashboard = buildDashboardPayload({
        bookings: bookings.data.bookings,
        tasks: tasks.data.task,
        catalog: catalog.data,
        stats: statistics.data.stats,
        now: Date.now(),
      });
      return {
        error: null,
        payload: { ...dashboard, capabilities: { bookingHistory: historyAvailable } },
      };
    } catch (error) {
      return { error: "Booking details could not be loaded. Please try again.", payload: null };
    }
  }, [bookings.data, bookings.error, tasks.data, tasks.error, catalog.data, catalog.error, statistics.data, statistics.error, historyAvailable]);

  currentPayload.current = result.payload;

  const retry = useCallback(async () => {
    if (retryActive.current) return false;
    retryActive.current = true;
    setRetrying(true);
    try {
      const outcomes = await Promise.allSettled([
        bookings.refetch(),
        tasks.refetch(),
        catalog.refetch(),
        statistics.refetch(),
      ]);
      return outcomes.every((outcome) => outcome.status === "fulfilled");
    } finally {
      retryActive.current = false;
      if (mounted.current) setRetrying(false);
    }
  }, [bookings.refetch, tasks.refetch, catalog.refetch, statistics.refetch]);

  const getPayload = useCallback(() => currentPayload.current, []);
  const { executeAction } = useBookingDashboardActions({ getPayload, onChanged: retry });

  const navigate = useCallback((message) => {
    if (message.action === "enquiries") {
      history.push("/enquiries");
      return;
    }
    const payload = currentPayload.current;
    if (!payload) throw new Error("Booking details are unavailable. Refresh the list and try again.");
    const booking = payload.bookings.find((entry) => entry.id === message.bookingId);
    const knownBooking = typeof message.bookingId === "string" && UUID.test(message.bookingId) && booking;
    const knownLead = typeof message.leadId === "string" && UUID.test(message.leadId)
      && (payload.bookings.some((entry) => entry.lead?.id === message.leadId) || payload.leadTasks.some((entry) => entry.leadId === message.leadId));
    const itemId = message.itemId;
    const boatId = message.boatId;
    const action = message.action;
    if (action === "lead") {
      if (!knownLead) throw new Error("This lead is unavailable. Refresh the list and try again.");
      history.push(`/leads/${message.leadId}`);
      return;
    }
    if (action === "conversation") {
      if (!knownBooking) throw new Error("This booking is unavailable. Refresh the list and try again.");
      history.push({ pathname: `/bookings/${booking.id}`, chatOpen: true });
      return;
    }
    if (["booking", "options", "quotes", "history"].includes(action)) {
      if (!knownBooking) throw new Error("This booking is unavailable. Refresh the list and try again.");
      if (action === "history" && !historyAvailable) throw new Error("Booking history is unavailable.");
      const suffix = action === "booking" ? "" : `/${action}`;
      history.push(`/bookings/${message.bookingId}${suffix}`);
      return;
    }
    if (!["new-booking", "edit-booking", "new-lead", "add-boat", "crew", "merge", "delete-booking", "phone"].includes(action)) {
      throw new Error("This action is unavailable.");
    }
    if (!["new-booking", "new-lead", "phone"].includes(action) && !knownBooking) throw new Error("This booking is unavailable. Refresh the list and try again.");
    if (message.leadId && !knownLead) throw new Error("This lead is unavailable. Refresh the list and try again.");
    if (itemId && (!UUID.test(itemId) || !booking?.items?.some((entry) => entry.id === itemId))) throw new Error("This boat item is unavailable.");
    if (boatId && (!UUID.test(boatId) || !payload.catalog.some((entry) => entry.id === boatId))) throw new Error("This boat is unavailable.");
    if (nativeRequestRef.current) throw new Error("Finish the open window before starting another action.");
    const request = {
      action,
      bookingId: message.bookingId,
      leadId: message.leadId,
      itemId,
      boatId,
      key: nativeSequence.current + 1,
    };
    nativeSequence.current += 1;
    if (action === "phone") {
      const contact = knownBooking ? booking.lead : knownLead
        ? payload.bookings.find((entry) => entry.lead?.id === message.leadId)?.lead || payload.leadTasks.find((entry) => entry.leadId === message.leadId) : null;
      const phone = typeof contact?.phone === "string" ? contact.phone.trim() : "";
      const digits = phone.replace(/[\s().-]/g, "");
      if (!/^\+?\d{5,15}$/.test(digits)) throw new Error("A number for direct calling is unavailable. Open the lead details to check the phone number.");
      request.phone = phone;
      request.phoneURI = `tel:${digits}`;
    }
    nativeRequestRef.current = request;
    setNativeRequest(request);
  }, [history, historyAvailable]);

  const refreshTasks = useCallback(async () => {
    try {
      const refreshed = await tasks.refetch();
      return Array.isArray(refreshed.data?.task);
    } catch (refreshError) {
      return false;
    }
  }, [tasks.refetch]);

  const createQuote = useCallback(async (booking, itemIds) => {
    if (!UUID.test(booking?.id)) {
      showNotification("This booking is unavailable. Refresh the list and try again.");
      return;
    }
    const bookingItemIDs = (booking.items || [])
      .map((item) => item.id)
      .filter((id) => itemIds.includes(id) && UUID.test(id));
    try {
      const { data } = await generateQuote({
        variables: { bookingID: booking.id, bookingItemIDs },
      });
      const id = data?.generateQuote;
      if (!UUID.test(id)) throw new Error("The quote could not be created.");
      const refreshed = await refreshTasks();
      showNotification(refreshed ? "Quote created" : "Quote created, but the latest tasks could not be loaded. Refresh the page to try again.");
      history.push(`/bookings/${booking.id}/quotes/${id}`);
    } catch (error) {
      showNotification(error.message || "The quote could not be created.");
    }
  }, [generateQuote, history, refreshTasks, showNotification]);

  const createOptions = useCallback(async (booking, itemIds) => {
    if (!UUID.test(booking?.id)) {
      showNotification("This booking is unavailable. Refresh the list and try again.");
      return;
    }
    const bookingItemIDs = (booking.items || [])
      .map((item) => item.id)
      .filter((id) => itemIds.includes(id) && UUID.test(id));
    if (!bookingItemIDs.length) {
      showNotification("Select at least one boat from this booking.");
      return;
    }
    try {
      const { data } = await generateOptions({
        variables: { bookingID: booking.id, bookingItemIDs },
      });
      const id = data?.generateOptions;
      if (!UUID.test(id)) throw new Error("The options could not be created.");
      const refreshed = await refreshTasks();
      showNotification(refreshed ? "Options created" : "Options created, but the latest tasks could not be loaded. Refresh the page to try again.");
      history.push(`/bookings/${booking.id}/options/${id}`);
    } catch (error) {
      showNotification(error.message || "The options could not be created.");
    }
  }, [generateOptions, history, refreshTasks, showNotification]);

  const closeNative = useCallback(() => {
    nativeRequestRef.current = null;
    setNativeRequest(null);
  }, []);

  const saveNative = useCallback((data) => {
    const request = nativeRequestRef.current;
    if (!request) return;
    nativeRequestRef.current = null;
    setNativeRequest(null);
    showNotification(request.action === "delete-booking" ? "Booking deleted" : request.action === "merge" ? "Bookings merged" : request.action === "crew" ? "Crew assignments saved"
      : request.action === "add-boat" ? "Boat added" : request.action === "new-lead" ? "Lead saved"
        : request.action === "new-options" ? "Options created" : "Booking saved");
    retry().then((refreshed) => {
      if (!refreshed || data.refreshRequired) showNotification("Saved, but the latest details could not be loaded. Refresh the page to try again.");
    });
    if (data.path) history.push(data.path);
  }, [retry, history, showNotification]);

  const loadConversation = useCallback(async ({ bookingId, leadId }) => {
    const payload = currentPayload.current;
    const booking = payload?.bookings.find((entry) => entry.id === bookingId);
    const knownLead = typeof leadId === "string" && UUID.test(leadId)
      && (payload?.bookings.some((entry) => entry.lead?.id === leadId) || payload?.leadTasks.some((entry) => entry.leadId === leadId));
    const validBooking = typeof bookingId === "string" && UUID.test(bookingId) && Boolean(booking);
    if (!knownLead || (bookingId && !(validBooking && booking.lead?.id === leadId))) {
      throw new Error("These details are unavailable.");
    }
    try {
      const response = await client.query({
        query: GET_DASHBOARD_CONVERSATION,
        variables: { leadID: leadId },
        fetchPolicy: "network-only",
        errorPolicy: "none",
      });
      const data = response.data?.messagesByLead;
      if (!Array.isArray(data)) throw new Error("unavailable");
      return data;
    } catch (error) {
      if (error.message === "These details are unavailable.") throw error;
      throw new Error("These details could not be loaded. Please try again.");
    }
  }, [client]);

  useEffect(() => {
    if (refreshEnabled) {
      bookings.startPolling(BOOKING_POLL_INTERVAL);
      tasks.startPolling(TASK_POLL_INTERVAL);
    } else {
      bookings.stopPolling();
      tasks.stopPolling();
    }
    return () => {
      bookings.stopPolling();
      tasks.stopPolling();
    };
  }, [refreshEnabled, bookings.startPolling, bookings.stopPolling, tasks.startPolling, tasks.stopPolling]);

  useEffect(() => () => { mounted.current = false; }, []);

  return (
    <>
      <BookingDashboard
        payload={result.payload}
        error={result.error}
        retrying={retrying}
        refreshEnabled={refreshEnabled}
        onRefreshEnabled={setRefreshEnabled}
        onRetry={retry}
        onAction={executeAction}
        onNavigate={navigate}
        onCreateQuote={createQuote}
        onCreateOptions={createOptions}
        onLoadConversation={loadConversation}
      />
      {nativeRequest ? (
        <BookingDashboardNativeDialog
          key={nativeRequest.key}
          request={nativeRequest}
          bookings={bookings.data?.bookings || []}
          catalog={catalog.data}
          onClose={closeNative}
          onSaved={saveNative}
          onChanged={retry}
        />
      ) : null}
    </>
  );
}
